import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Chip, Dialog, Grid, Typography, useMediaQuery } from "@mui/material";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import BusinessIcon from "@mui/icons-material/Business";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";

import Translate from "../../utils/Translate";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { IconAndLabelColumnHeader, TableAvatar2 } from "../../utils/dataGrid";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import usePagination from "../../utils/pagination";
import DataGridEasyFilters from "../DataGridEasyFilters";
import { useApi } from "../../utils/Api";
import { useNavigate } from "react-router-dom";
import MarketNoteCard from "./MarketNoteCard";
import { formatDate } from "../../utils/dateTimeFormat";
import PermitCard from "../actor-permits/PermitCard";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import LabelIcon from "@mui/icons-material/Label";
import CheckIcon from "@mui/icons-material/Check";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import DotMenu from "../DotMenu";
import AddOrUpdateMarketNote from "./dialogs/AddOrUpdateMarketNote";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HowToRegIcon from "@mui/icons-material/HowToReg";

function buildPaginationFilter() {
  return null;
}

const MarketNotesList = ({ searchText, defaultSort, defaultStdFilter, onReload, onLoaded, setIsLoading }) => {
  const [paginationKey] = useState("MARKETNOTES_GRID");
  const api = useApi();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [ePortUserId, setEPortUserId] = useState(() => sessionStorage.getItem("ePortUserId") || "");

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedMarketNote, setSelectedMarketNote] = useState(null);

  const apiRef = useGridApiRef();
  const navigate = useNavigate();

  const loadMarketNotes = useCallback(
    async (queryModel) => {
      // Avoid unnecessary call to get-page when navigating
      if (!setIsLoading) {
        return { rows: [], rowCount: 0 };
      }
      setIsLoading(true);

      try {
        const url = `${import.meta.env.VITE_MAIN_URL}customerrelations/marketnotes/get-page`;
        const response = await api.fetch(url, queryModel, "POST");
        if (onLoaded) {
          onLoaded();
        }
        if (response && response.isSuccessful) {
          return {
            rows: response.rows,
            rowCount: response.rowCount,
          };
        }
        return { rows: [], rowCount: 0 };
      } finally {
        setIsLoading(false);
      }
    },
    [api, onLoaded, setIsLoading],
  );

  const { localUpdateRows, reloadData, resetPagination, ...pagination } = usePagination(
    loadMarketNotes,
    searchText,
    [
      {
        field: "regDate",
        sort: "desc",
      },
    ],
    null,
    [50, 250, 1000],
    paginationKey,
    buildPaginationFilter(),
    true,
  );

  const easyFilters = useMemo(
    () => [
      {
        id: "MyMarketNotes",
        name: Translate.get("MyMarketNotes"),
        description: Translate.get("MyMarketNotesDescription"),
        filterModel: {
          items: [
            {
              id: "MyMarketNotes-responsibleEPortUserId",
              field: "responsibleEPortUserId",
              operator: "=",
              value: ePortUserId,
            },
          ],
          logicOperator: "and",
        },
      },

      {
        id: "CurrentMarketNotes",
        name: Translate.get("CurrentMarketNotes"),
        description: Translate.get("CurrentMarketNotesDescription"),
        filterModel: {
          items: [
            {
              id: "CurrentMarketNotes-NotificationDate",
              field: "notificationDate",
              operator: "between",
              value: [new Date(Date.now() - 31 * 24 * 60 * 60 * 1000), new Date(Date.now() + 31 * 24 * 60 * 60 * 1000)], // -+ 31 days
            },
          ],
          logicOperator: "and",
        },
      },
      {
        id: "MyTodoNotes",
        name: Translate.get("MyTodoNotes"),
        description: Translate.get("MyTodoNotesDescription"),
        filterModel: {
          items: [
            {
              id: "MyTodoNotes-responsible",
              field: "responsibleEPortUserId",
              operator: "=",
              value: ePortUserId,
            },
            {
              id: "MyTodoNotes-isDone",
              field: "isDone",
              operator: "is",
              value: false,
            },
          ],
          logicOperator: "and",
        },
        sortModel: [
          {
            field: "notificationDate",
            sort: "desc",
          },
        ],
      },
    ],
    [ePortUserId],
  );

  const localizedTextsMap = dataGridLang();

  async function handleReload() {
    await reloadData();
    if (onReload) {
      await onReload();
    }
  }

  // Define listViewColumn for small screens
  const listViewColDef = {
    field: "listColumn",
    display: "flex",
    renderCell: (params) => {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: "530px",
            boxSizing: "border-box",
            padding: "8px 0",
          }}
        >
          <MarketNoteCard
            marketNote={params.row}
            key={params.row.marketNoteId}
            menuItems={getDotMenuItems(params.row)}
          />
        </div>
      );
    },
  };

  // Define columns for market notes
  let columns = [
    {
      field: "companyActorName",
      headerName: Translate.get("Company"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={BusinessIcon} label={Translate.get("Company")} />,
      display: "flex",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <TableAvatar2
            image={params.row.companyActorImage}
            name={params.row.companyActorName}
            email={params.row.companyActorEmail}
          />
        </div>
      ),
    },
    {
      field: "contactPersonActorName",
      headerName: Translate.get("ContactPerson"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={HeadsetMicIcon} label={Translate.get("ContactPerson")} />,
    },
    {
      field: "textNote",
      headerName: Translate.get("Note"),
      disableColumnMenu: true,
      sortable: false,
      flex: 2,
      renderHeader: () => <IconAndLabelColumnHeader icon={StickyNote2Icon} label={Translate.get("Note")} />,
    },
    {
      field: "notificationDate",
      headerName: Translate.get("NotificationDate"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={DateRangeIcon} label={Translate.get("NotificationDate")} />,
      valueGetter: (value, row, column, apiRef) => (row.notificationDate ? formatDate(row.notificationDate) : ""),
    },
    {
      field: "isDone",
      headerName: Translate.get("Ready"),
      disableColumnMenu: true,
      flex: 0.5,
      type: "boolean",
      renderHeader: () => <IconAndLabelColumnHeader icon={CheckCircleIcon} label={Translate.get("Ready")} />,
      renderCell: (params) =>
        params.row.isDone ? (
          <CheckIcon sx={{ color: "success.main" }} />
        ) : (
          <HourglassTopIcon sx={{ color: "text.disabled" }} />
        ),
    },
    {
      field: "regDate",
      headerName: Translate.get("Created"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={DateRangeIcon} label={Translate.get("Created")} />,
      valueGetter: (value, row, column, apiRef) => (row.regDate ? formatDate(row.regDate) : ""),

      // row.validFrom ? formatDate(row.validFrom) : ""
    },
    {
      field: "responsibleEPortUserId",
      headerName: "Systemstatus: Id Ansvarig",
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={HowToRegIcon} label={Translate.get("Responsible")} />,
    },
    {
      field: "responsibleEPortUserName",
      headerName: Translate.get("Responsible"),
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={HowToRegIcon} label={Translate.get("Responsible")} />,
    },
    {
      field: "marketNoteLabels",
      headerName: Translate.get("Labels"),
      disableColumnMenu: true,
      flex: 1,
      sortable: false,
      renderHeader: () => <IconAndLabelColumnHeader icon={LabelIcon} label={Translate.get("Labels")} />,
      valueGetter: (value, row) => row.marketNoteLabels?.map((label) => label.labelText).join(", ") || "",
    },

    {
      field: "dotview",
      headerName: "Menu",
      disableColumnMenu: true,
      flex: 0,
      maxWidth: 50,
      align: "right",
      sortable: false,
      filterable: false,
      disableExport: true,
      renderHeader: () => <div></div>,
      display: "flex",
      renderCell: (params) => <DotMenu content={getDotMenuItems(params.row)} horizontal />,
    },
  ];

  const hiddenFields = ["responsibleEPortUserId"];

  const getTogglableColumns = (columns) =>
    columns.filter((column) => !hiddenFields.includes(column.field)).map((column) => column.field);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ responsibleEPortUserId: false });

  const navigateToActor = (actorId) => {
    navigate(`/dashboard/company/${actorId}`, { replace: false });
  };

  // Find the selected filter based on the current filter model. Used when coming back from an actor
  const selectedFilter = useMemo(() => {
    if (!pagination.filterModel?.items?.length) {
      return null;
    }
    return easyFilters.find((filter) => {
      const filterItems = filter.filterModel.items;
      const currentItems = pagination.filterModel.items;
      if (filterItems.length !== currentItems.length) {
        return false;
      }
      return filterItems.every((item, index) => {
        const currentItem = currentItems[index];
        return (
          item.field === currentItem.field && item.operator === currentItem.operator && item.value === currentItem.value
        );
      });
    });
  }, [pagination.filterModel, easyFilters]);

  const getDotMenuItems = (note) => {
    const menudata = [
      {
        icon: <EditIcon />,
        text: Translate.get("Alter"),
        onClick: () => handleOpenEdit(note),
      },
      {
        icon: <CheckIcon />,
        text: Translate.get("ClearMark"),
        disabled: note.isDone === true,
        onClick: () => {
          clearMarkNote(note);
        },
      },
      // {
      //   icon: <DeleteIcon />,
      //   text: Translate.get("Delete"),
      //   onClick: () => {
      //     setNoteToDelete(note);
      //     setIsDeleteConfirmOpen(true);
      //   },
      // },
    ];
    return menudata;
  };

  async function clearMarkNote(note) {
    setIsLoading(true);

    try {
      let reqObj = {
        marketNoteId: note.marketNoteId,
        text: note.textNote,
        isDone: true,
        notificationDate: note.notificationDate,
        companyActorId: note.companyActorId,
        contactPersonActorId: note.contactPersonActorId,
        responsibleEPortUserId: note.responsibleEPortUserId,
        marketNoteLabelIds: note.marketNoteLabels
          ? note.marketNoteLabels.map((label) => label.marketNoteLabelId)
          : null,
      };

      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}customerrelations/marketnote`, reqObj, "PUT");
    } finally {
      setIsLoading(false);
      await handleReload();
    }
  }

  function handleOpenEdit(note) {
    setSelectedMarketNote(note);
    setIsEditOpen(true);
  }

  function handleCloseEdit() {
    setIsEditOpen(false);
    setSelectedMarketNote(null);
  }

  async function handleEditSuccess() {
    setIsEditOpen(false);
    setSelectedMarketNote(null);

    await handleReload();
  }

  return (
    <>
      <Box paddingTop={2} paddingLeft={1} paddingBottom={1} paddingRight={1}>
        <DataGridEasyFilters
          filters={easyFilters}
          currentFilterModel={pagination.filterModel}
          onSelectFilter={(filter) => {
            pagination.onFilterModelChange(filter?.filterModel);
            if (filter?.sortModel) {
              pagination.onSortModelChange(filter.sortModel);
            } else {
              pagination.onSortModelChange([
                {
                  field: "regDate",
                  sort: "desc",
                },
              ]);
            }

            pagination.onPaginationModelChange({
              ...pagination.paginationModel,
              page: 0,
            });
          }}
          selectedFilter={selectedFilter}
        />
      </Box>

      <DataGridPremium
        showToolbar
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        className="stdVisible dgDynIconText"
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) => row.marketNoteId}
        localeText={localizedTextsMap}
        autoHeight={true}
        rowHeight={isSmallScreen ? 530 : 95}
        getDetailPanelContent={(params) => (
          <Box sx={{ p: 2, pl: 7 }} style={{ backgroundColor: "#F4F6F8" }}>
            <Grid container spacing={4}>
              <Grid item size={{ md: 6, xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {Translate.get("Note")}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                  {params.row.textNote}
                </Typography>
              </Grid>

              <Grid item size={{ md: 6, xs: 6 }}>
                {params.row.marketNoteLabels?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {Translate.get("Labels")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {params.row.marketNoteLabels.map((label) => (
                        <Chip key={label.marketNoteLabelId} label={label.labelText} />
                      ))}
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
        getDetailPanelHeight={() => "auto"}
        apiRef={apiRef}
        slots={{
          toolbar: () =>
            ToolBar({
              hideFilter: true,
              showExport: true,
              exportFileName: Translate.get("MarketNotes"),
              apiRef: apiRef,
              wide: true,
            }),
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
          noResultsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
          detailPanelExpandIcon: KeyboardArrowRightIcon,
          detailPanelCollapseIcon: KeyboardArrowDownIcon,
        }}
        {...(!isSmallScreen
          ? {
              onRowClick: (params) => {
                navigateToActor(params.row.companyActorId);
              },
            }
          : {})}
        slotProps={{
          columnsPanel: {
            getTogglableColumns,
          },
        }}
        disableDensitySelector
        disableColumnSelector={isSmallScreen}
        listView={isSmallScreen}
        listViewColumn={isSmallScreen ? listViewColDef : undefined}
        filterMode={pagination.filterMode}
        sortingMode={pagination.sortingMode}
        paginationMode={pagination.paginationMode}
        filterModel={pagination.filterModel}
        sortModel={pagination.sortModel}
        onFilterModelChange={pagination.onFilterModelChange}
        onSortModelChange={pagination.onSortModelChange}
        onPaginationModelChange={pagination.onPaginationModelChange}
        rows={pagination.rows}
        rowCount={pagination.rowCount}
        loading={pagination.isLoading}
        pagination={pagination.pagination}
        pageSizeOptions={pagination.pageSizeOptions}
        paginationModel={pagination.paginationModel}
      />

      <Dialog open={isEditOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <AddOrUpdateMarketNote
          actor={null}
          type={null}
          existingMarketNote={selectedMarketNote}
          onClose={handleCloseEdit}
          onSuccess={handleEditSuccess}
        />
      </Dialog>
    </>
  );
};

export default MarketNotesList;
